/**
 * LocationsMapDialog - Manages modal dialogs for location details
 * Provides static methods for opening and closing dialogs with location information
 */
class LocationsMapDialog {
    /**
     * Opens a dialog with location details or updates existing dialog content
     * @param {HTMLElement} content - The HTML element containing dialog content
     * @param {HTMLElement} [container=document.body] - The container where dialog is appended
     * @throws {Error} If content element is not provided
     * @returns {void}
     */
    static open(content, container = document.body) {
        if (!content || !(content instanceof HTMLElement)) {
            console.error('LocationsMapDialog.open: Invalid content element provided');
            return;
        }

        let dialog = document.querySelector('.locations-map-dialog');

        if (dialog) {
            const contentContainer = dialog.querySelector('.locations-map-dialog__content');
            if (contentContainer) {
                // Clear existing content safely
                contentContainer.innerHTML = '';
                contentContainer.appendChild(content);
            }
            return;
        }

        dialog = document.createElement('div');
        dialog.classList.add('locations-map-dialog');
        
        // Create close button safely using DOM API
        const closeButton = document.createElement('button');
        closeButton.className = 'locations-map-dialog__close';
        closeButton.setAttribute('aria-label', 'Close');
        const closeIcon = document.createElement('i');
        closeIcon.className = 'icon-times';
        closeButton.appendChild(closeIcon);

        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.className = 'locations-map-dialog__content';
        contentContainer.appendChild(content);

        // Append elements
        dialog.appendChild(closeButton);
        dialog.appendChild(contentContainer);

        container.appendChild(dialog);

        // Add event listener
        closeButton.addEventListener('click', () => {
            LocationsMapDialog.close();
        });
    }

    /**
     * Closes the currently open locations map dialog and dispatches event
     * @returns {void}
     */
    static close() {
        const existingDialog = document.querySelector('.locations-map-dialog');

        if (existingDialog) {
            existingDialog.remove();

            const event = new CustomEvent('dialog-closed.syn.locationsMap', {
                detail: {
                    message: 'The locations map dialog has been closed',
                    time: new Date(),
                },
            });
            document.dispatchEvent(event);
        }
    }
}

/**
 * LocationsMap - Manages Google Maps display with custom location markers
 * Handles map initialization, marker rendering, location data parsing, and dialog interactions
 */
class LocationsMap {
    eventNamespace = '.syn.locationsMap';

    /**
     * Initializes LocationsMap instance
     * @param {HTMLElement} container - The DOM element containing the map and location data
     * @throws {Error} If container is invalid or not provided
     */
    constructor(container) {
        if (!container || !(container instanceof HTMLElement)) {
            console.error('LocationsMap: Invalid container element provided');
            throw new Error('LocationsMap requires a valid HTMLElement container');
        }

        this.container = container;
        this.$container = jQuery(container);
        this.map = null;
        this.mapOptions = {
            zoom: (() => {
                let zoomLevel = 5;

                if (window.innerWidth <= 1200 && window.innerWidth > 768) {
                    zoomLevel = 4;
                } else if (window.innerWidth <= 768) {
                    zoomLevel = 3;
                }

                return zoomLevel;
            })(),
            center: { lat: 39.8283, lng: -98.5795 },
            mapId: this.container.id,
            disableDefaultUI: true,
            zoomControl: true,
        };
        this.locations = new Map();
        this.activeLocation = null;
        this.markers = new Map();
        this.markerOptions = {
            gmpClickable: true
        }

        this.initMap();
        this.listen();
    }

    /**
     * Emits a custom event with the component namespace
     * @param {string} eventName - The event name (without namespace)
     * @param {Object} options - Event options including detail object
     * @returns {void}
     */
    emit(eventName, options = {}) {
        const defaults = {
            bubbles: true,
            cancelable: true,
            detail: {}
        };
        const eventConfig = { ...defaults, ...options };
        const event = new CustomEvent(`${eventName}${this.eventNamespace}`, eventConfig);
        document.dispatchEvent(event);
    }

    /**
     * Validates geographic coordinates
     * @param {number} latitude - The latitude value
     * @param {number} longitude - The longitude value
     * @returns {Object|null} Object with lat and lng if valid, null if invalid
     */
    validateCoordinates(latitude, longitude) {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        if (isNaN(lat) || isNaN(lng)) {
            console.warn('LocationsMap: Invalid coordinate format provided');
            return null;
        }

        if (lat < -90 || lat > 90) {
            console.warn('LocationsMap: Latitude out of valid range (-90 to 90):', lat);
            return null;
        }

        if (lng < -180 || lng > 180) {
            console.warn('LocationsMap: Longitude out of valid range (-180 to 180):', lng);
            return null;
        }

        return { lat, lng };
    }

    /**
     * Validates and sanitizes phone number
     * @param {string} phone - The phone number to validate
     * @returns {string|null} Sanitized phone number or null if invalid
     */
    sanitizePhoneNumber(phone) {
        if (!phone || typeof phone !== 'string') {
            return null;
        }

        // Allow only digits, spaces, hyphens, parentheses, and plus sign
        const cleanPhone = phone.replace(/[^\d\-\+\(\)\s]/g, '').trim();

        if (cleanPhone.length === 0) {
            return null;
        }

        return cleanPhone;
    }

    /**
     * Retrieves and parses location data from hidden data elements
     * Validates all data before storing in locations map
     * @returns {Map|null} Map of location data keyed by location title, or null if no data element found
     */
    get locationData() {
        const hiddenDataElement = this.container.querySelector('.dpt-locations-map__data');
        const locations = new Map();

        if (!hiddenDataElement) {
            console.warn('LocationsMap: No location data element found');
            return null;
        }

        hiddenDataElement.querySelectorAll('.dpt-locations-map__location-data').forEach((locationElement, index) => {
            try {
                const latitude = locationElement.getAttribute('data-latitude');
                const longitude = locationElement.getAttribute('data-longitude');
                
                // Validate coordinates
                const coordinates = this.validateCoordinates(latitude, longitude);
                if (!coordinates) {
                    console.warn(`LocationsMap: Skipping location ${index + 1} - invalid coordinates`);
                    return;
                }

                // Sanitize phone number if present
                const phone = locationElement.getAttribute('data-phone');
                const cleanPhone = phone ? this.sanitizePhoneNumber(phone) : '';

                const location = {
                    number: locationElement.getAttribute('data-number') || '',
                    title: locationElement.getAttribute('data-title') || `Location ${index + 1}`,
                    latitude: coordinates.lat,
                    longitude: coordinates.lng,
                    address: locationElement.getAttribute('data-address') || '',
                    phone: cleanPhone,
                    isHeadquarters: locationElement.getAttribute('data-is-headquarters') === '1',
                };

                locations.set(location.title, location);
            } catch (error) {
                console.error(`LocationsMap: Error parsing location data at index ${index}:`, error);
            }
        });

        this.locations = locations;
        return locations;
    }

    /**
     * Initializes the Google Map and loads locations
     * @async
     * @returns {Promise<void>}
     */
    async initMap() {
        try {
            const { Map } = await google.maps.importLibrary("maps");
            const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

            const mapElement = this.container.querySelector('.dpt-locations-map__map');
            if (!mapElement) {
                console.error('LocationsMap: Map container element not found');
                this.emit('error', { 
                    detail: { message: 'Map element not found' } 
                });
                return;
            }

            this.map = new Map(mapElement, this.mapOptions);
            await this.loadLocations();
        } catch (error) {
            console.error('LocationsMap: Failed to initialize Google Maps:', error);
            this.emit('error', { 
                detail: { message: 'Google Maps initialization failed', error: error.message } 
            });
        }
    }

    /**
     * Attaches event listeners for location cards and dialog events
     * @returns {void}
     */
    listen() {
        // Listen for location card clicks
        this.$container.on('click', '.card--location', (e) => {
            const locationTitle = e.currentTarget.getAttribute('data-location-title');
            const marker = this.markers.get(locationTitle);

            if (marker) {
                const location = this.locationData.get(locationTitle);
                this.handleMarkerClick(location);

                setTimeout(() => {
                    window.scrollTo({
                        top: this.container.offsetTop + 300,
                        behavior: 'smooth'
                    });
                }, 300);
            }
        });

        // Listen for dialog close events
        document.addEventListener(`dialog-closed${this.eventNamespace}`, this.reset.bind(this));
    }

    /**
     * Creates and adds a marker to the map
     * @async
     * @param {Object} location - Location object with title and data
     * @param {Object} args - Arguments to pass to AdvancedMarkerElement constructor
     * @returns {Promise<AdvancedMarkerElement>} The created marker instance
     */
    async addMarker(location, args) {
        try {
            const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
            const marker = new AdvancedMarkerElement({ map: this.map, ...args });
            this.markers.set(location.title, marker);
            return marker;
        } catch (error) {
            console.error(`LocationsMap: Failed to add marker for location "${location.title}":`, error);
            throw error;
        }
    }

    /**
     * Opens a dialog with location information
     * Uses safe DOM manipulation to prevent XSS attacks
     * @async
     * @param {Object} location - Location object with title, address, phone, number
     * @param {string} [color='#012260'] - Hex color for the location marker
     * @returns {Promise<void>}
     */
    async openDialog(location, color = '#012260') {
        try {
            const content = document.createElement('div');
            content.classList.add('dialog-content-wrapper');

            if (location.isHeadquarters) {
                content.classList.add('is-headquarters');
            }

            // Title section - use textContent for security
            const titleDiv = document.createElement('div');
            titleDiv.className = 'item item--title';
            
            const numberSpan = document.createElement('span');
            numberSpan.className = 'number';
            numberSpan.textContent = location.number;
            
            const titleStrong = document.createElement('strong');
            titleStrong.textContent = location.title;
            
            titleDiv.appendChild(numberSpan);
            titleDiv.appendChild(titleStrong);
            content.appendChild(titleDiv);

            // Address section - use textContent for security
            if (location.address) {
                const addressDiv = document.createElement('div');
                addressDiv.className = 'item item--address';
                
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('class', 'icon');
                svg.setAttribute('width', '20px');
                svg.setAttribute('height', '20px');
                svg.setAttribute('fill', 'currentColor');
                const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
                use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#flag');
                svg.appendChild(use);
                
                const addressSpan = document.createElement('span');
                addressSpan.className = 'text';
                addressSpan.textContent = location.address;
                
                addressDiv.appendChild(svg);
                addressDiv.appendChild(addressSpan);
                content.appendChild(addressDiv);
            }

            // Phone section - validate and use textContent
            if (location.phone) {
                const phoneDiv = document.createElement('div');
                phoneDiv.className = 'item item--phone';
                
                const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('class', 'icon');
                svg.setAttribute('width', '20px');
                svg.setAttribute('height', '20px');
                svg.setAttribute('fill', 'currentColor');
                const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
                use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '#phone');
                svg.appendChild(use);
                
                const phoneLink = document.createElement('a');
                phoneLink.className = 'text';
                phoneLink.href = `tel:${location.phone}`;
                phoneLink.setAttribute('rel', 'prerender');
                phoneLink.textContent = location.phone;
                
                phoneDiv.appendChild(svg);
                phoneDiv.appendChild(phoneLink);
                content.appendChild(phoneDiv);
            }

            LocationsMapDialog.open(content, this.$container.find('.dpt-locations-map__map')[0]);
        } catch (error) {
            console.error('LocationsMap: Failed to open dialog:', error);
            this.emit('error', { detail: { message: 'Failed to open location dialog' } });
        }
    }

    /**
     * Loads all locations and renders their markers on the map
     * Uses Promise.allSettled for graceful error handling
     * @async
     * @returns {Promise<void>}
     */
    async loadLocations() {
        const locations = this.locationData;

        if (!locations || locations.size === 0) {
            console.warn('LocationsMap: No locations to load');
            return;
        }

        try {
            const results = await Promise.allSettled(
                Array.from(locations.values()).map(location => this.renderMarker(location))
            );

            // Log any failed marker renders
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.warn(`LocationsMap: Failed to render marker ${index + 1}:`, result.reason);
                }
            });
        } catch (error) {
            console.error('LocationsMap: Error loading locations:', error);
            this.emit('error', { detail: { message: 'Failed to load locations' } });
        }
    }

    /**
     * Renders a single location marker on the map
     * @async
     * @param {Object} location - Location object with coordinates and data
     * @returns {Promise<void>}
     */
    async renderMarker(location) {
        const color = location.isHeadquarters ? '#F97A00' : '#012260';
        const markerOptions = {
            position: { lat: location.latitude, lng: location.longitude },
            title: location.title,
            content: this.getMarker(location.number, color),
            ...this.markerOptions
        };

        const marker = await this.addMarker(location, markerOptions);
        marker.addEventListener("gmp-click", this.handleMarkerClick.bind(this, location));
    }

    /**
     * Resets all markers to their default sizes
     * Updates active location marker to large size if applicable
     * @returns {void}
     */
    resetAllMarkers() {
        this.locations.forEach(location => {
            let size = 30;

            if (this.activeLocation && location.title === this.activeLocation.title) {
                size = 47;
            }

            const marker = this.markers.get(location.title);

            if (marker) {
                const color = location.isHeadquarters ? '#F97A00' : '#012260';

                marker.setMap(null);
                marker.content = this.getMarker(location.number, color, size);
                marker.setMap(this.map);
            }
        });
    }

    /**
     * Resets the map to initial state
     * Closes dialog and resets markers
     * @returns {void}
     */
    reset() {
        this.activeLocation = null;

        if (this.map) {
            this.map.setZoom(this.mapOptions.zoom);
            this.map.setCenter(this.mapOptions.center);
            this.resetAllMarkers();
        }
    }

    /**
     * Handles marker click events
     * Updates marker size, centers map, and opens location dialog
     * @param {Object} location - Location object associated with the marker
     * @returns {void}
     */
    handleMarkerClick(location) {
        const marker = this.markers.get(location.title);

        if (!marker) {
            console.warn('LocationsMap: Marker not found for location:', location.title);
            return;
        }

        // Update marker size and z-index by re-adding
        marker.setMap(null);
        marker.content = this.getMarker(location.number, location.isHeadquarters ? '#F97A00' : '#012260', 47);
        marker.setMap(this.map);

        // Calculate center with mobile offset
        let center = { lat: location.latitude, lng: location.longitude };

        if (window.innerWidth <= 768) {
            // On mobile, offset center to account for dialog overlay
            const latOffset = 0.035;
            center.lat = center.lat + latOffset;
        }

        this.map.setCenter(center);
        this.map.setZoom(12);

        this.openDialog(location, location.isHeadquarters ? '#F97A00' : '#012260');

        this.activeLocation = location;
    }

    /**
     * Generates SVG marker element with custom styling
     * Uses DOM API to safely create SVG without XSS risk
     * @param {number} number - Location number to display on marker
     * @param {string} color - Hex color for the marker
     * @param {number} [size=30] - Size of the marker in pixels
     * @returns {HTMLDivElement} Container with SVG marker content
     */
    getMarker(number, color, size = 30) {
        const isLargeSize = size >= 45;
        const transformValue = isLargeSize ? `translate(1, 2) scale(1.08)` : '';

        // Shadow path scaled coordinates
        const s1 = Math.round(size * 0.55);
        const s2 = Math.round(size * -0.1);
        const s3 = Math.round(size * 0.8167);
        const s4 = Math.round(size * 0.8);
        const s5 = Math.round(size * 0.6134);
        const s6 = Math.round(size * 0.4668);
        const s7 = Math.round(size * 0.9667);
        const s8 = Math.round(size * 0.2134);
        const s9 = Math.round(size * -0.0167);
        const s10 = Math.round(size * -0.2833);

        // Checkmark path scaled coordinates
        const x1 = Math.round(size * 0.176296);
        const y1 = Math.round(size * 0.791667);
        const x2 = Math.round(size * 0.622478);
        const x3 = Math.round(size * 0.631831);
        const x4 = Math.round(size * 0.636065);
        const x5 = Math.round(size * 0.622133);
        const y2 = Math.round(size * 0.803361);
        const y3 = Math.round(size * 0.808015);
        const x6 = Math.round(size * 0.406508);
        const y4 = Math.round(size * 0.994666);
        const x7 = Math.round(size * 0.401);
        const x8 = Math.round(size * 0.396);
        const x9 = Math.round(size * 0.391);
        const x10 = Math.round(size * 0.171);
        const x11 = Math.round(size * 0.167);
        const x12 = Math.round(size * 0.176);
        const textX = Math.round(size / 2);
        const textY = Math.round(size / 2);
        const fontSize = Math.round(size * 0.6);

        // Create container
        const container = document.createElement('div');

        // Create SVG element using createElementNS for proper namespace
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', size);
        svg.setAttribute('height', size);
        svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
        svg.setAttribute('style', 'overflow: visible;');

        // Create defs element
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

        // Create linear gradient
        const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
        gradient.setAttribute('id', 'a');
        gradient.setAttribute('x1', '50%');
        gradient.setAttribute('x2', '50%');
        gradient.setAttribute('y1', '0%');
        gradient.setAttribute('y2', '100%');

        const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stop1.setAttribute('offset', '0%');
        stop1.setAttribute('stop-opacity', '.4');

        const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stop2.setAttribute('offset', '83.105%');

        gradient.appendChild(stop1);
        gradient.appendChild(stop2);

        // Create filter
        const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
        filter.setAttribute('id', 'b');
        filter.setAttribute('width', '276%');
        filter.setAttribute('height', '226.7%');
        filter.setAttribute('x', '-104%');
        filter.setAttribute('y', '-66.7%');
        filter.setAttribute('filterUnits', 'objectBoundingBox');

        const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
        feGaussianBlur.setAttribute('in', 'SourceGraphic');
        feGaussianBlur.setAttribute('stdDeviation', '6');

        filter.appendChild(feGaussianBlur);

        defs.appendChild(gradient);
        defs.appendChild(filter);

        // Create main group
        const mainGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        mainGroup.setAttribute('fill', 'none');
        mainGroup.setAttribute('fill-rule', 'evenodd');
        mainGroup.setAttribute('transform', 'translate(0 0)');

        // Create shadow path
        const shadowPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        shadowPath.setAttribute('fill', 'url(#a)');
        shadowPath.setAttribute('d', `M${s1},${s2} L${s3},${s4} L${s5},${s4} L${s6},${s7} L${s8},${s4} L${s9},${s4} L${s10},${s2} L${s1},${s2} Z`);
        shadowPath.setAttribute('filter', 'url(#b)');
        shadowPath.setAttribute('opacity', '.4');

        mainGroup.appendChild(shadowPath);

        // Create marker group
        const markerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        markerGroup.setAttribute('transform', 'translate(3)');

        // Create background rect
        const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bgRect.setAttribute('width', size - 7);
        bgRect.setAttribute('height', size - 7);
        bgRect.setAttribute('x', '-.5');
        bgRect.setAttribute('y', '0');
        bgRect.setAttribute('rx', '2');
        bgRect.setAttribute('fill', color);

        markerGroup.appendChild(bgRect);

        // Create white overlay rect
        const overlayRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        overlayRect.setAttribute('width', size - 7);
        overlayRect.setAttribute('height', size - 7);
        overlayRect.setAttribute('x', '.5');
        overlayRect.setAttribute('y', '1');
        overlayRect.setAttribute('fill', '#FFF');
        overlayRect.setAttribute('fill-opacity', '.85');
        overlayRect.setAttribute('stroke', color);
        overlayRect.setAttribute('stroke-linejoin', 'square');
        overlayRect.setAttribute('stroke-width', '2');
        overlayRect.setAttribute('rx', '2');

        markerGroup.appendChild(overlayRect);

        // Create checkmark group (with optional transform for large size)
        const checkmarkGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        if (transformValue) {
            checkmarkGroup.setAttribute('transform', transformValue);
        }

        // Create checkmark path
        const checkmarkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        checkmarkPath.setAttribute('fill', color);
        checkmarkPath.setAttribute('d', `M${x1},${y1} L${x2},${y1} C${x3},${y1} ${x4},${y2} ${x5},${y3} L${x6},${y4} C${x7},${y4} ${x8},${y4} ${x9},${y4} L${x10},${y3} C${x11},${y2} ${x10},${y1} ${x12},${y1} Z`);

        checkmarkGroup.appendChild(checkmarkPath);
        markerGroup.appendChild(checkmarkGroup);

        mainGroup.appendChild(markerGroup);

        // Create text element
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', textX);
        text.setAttribute('y', textY);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        text.setAttribute('font-size', fontSize);
        text.setAttribute('fill', color);
        text.setAttribute('font-family', 'Barlow, sans-serif');
        text.setAttribute('font-weight', '600');
        text.textContent = number;

        // Assemble SVG
        svg.appendChild(defs);
        svg.appendChild(mainGroup);
        svg.appendChild(text);

        // Add SVG to container
        container.appendChild(svg);

        return container;
    }
}

/**
 * Document Ready Handler - Initializes LocationsMap for all map containers
 * Safely handles initialization errors and provides console feedback
 */
jQuery(document).ready(function ($) {
    $('.dpt-locations-map').each(function (index) {
        try {
            const container = this;
            const map = new LocationsMap(container);
        } catch (error) {
            console.error(`Failed to initialize LocationsMap instance ${index + 1}:`, error);
        }
    });
});